import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { getBackendURL } from '../utils/backendURL';
import { showSuccess, showError, showWarning, showInfo } from '../utils/notifications';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { 
  X, 
  Play, 
  Square, 
  Save, 
  Loader2,
  MousePointer,
  Type,
  Globe,
  ArrowRight,
  ChevronRight,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = getBackendURL();

const LiveRecorder = ({ isOpen, onClose, user, environments = [], existingTest = null }) => {
  // Helper function to extract selector value (handles both string and object)
  const getSelectorValue = (selector) => {
    if (!selector) return '';
    if (typeof selector === 'string') return selector;
    if (typeof selector === 'object' && selector.value) return selector.value;
    return '';
  };

  // State management
  const [step, setStep] = useState('setup'); // loading, setup, recording, saving
  const [sessionId, setSessionId] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isBrowserReady, setIsBrowserReady] = useState(false); // Track if browser proxy is ready
  
  // Setup form
  const [startingUrl, setStartingUrl] = useState('');
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [useCustomUrl, setUseCustomUrl] = useState(true);
  
  // Recording state
  const [steps, setSteps] = useState([]);
  const [currentUrl, setCurrentUrl] = useState('');
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [iframeKey, setIframeKey] = useState(0);
  const [currentScreenshot, setCurrentScreenshot] = useState(null);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputText, setInputText] = useState('');
  
  // Step editing state
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  
  // Resume recording state
  const [isResuming, setIsResuming] = useState(false);
  const [resumingTestId, setResumingTestId] = useState(null);
  
  const iframeRef = useRef(null);
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  
  // Debounce refs for input handling
  const inputDebounceTimers = useRef({});
  const lastInputStepIndex = useRef({}); // Track last step index for each selector
  
  // Save form
  const [testName, setTestName] = useState('');
  const [testDescription, setTestDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Handle state when modal opens/closes or existingTest changes
  useEffect(() => {
    console.log('[LiveRecorder useEffect] State change detected:', { 
      isOpen, 
      hasExistingTest: !!existingTest,
      existingTestId: existingTest?.id,
      currentStep: step 
    });
    
    if (isOpen) {
      if (existingTest) {
        console.log('[LiveRecorder useEffect] Opening with existing test, setting step to loading');
        setStep('loading');
        setLoadingMessage('Preparing to resume recording...');
      } else {
        console.log('[LiveRecorder useEffect] Opening for new recording, setting step to setup');
        setStep('setup');
        setLoadingMessage('');
      }
    } else {
      // Reset to setup when modal closes (for next open)
      console.log('[LiveRecorder useEffect] Modal closed, resetting state');
      setStep('setup');
      setSessionId(null);
      setIsResuming(false);
      setResumingTestId(null);
      setLoadingMessage('');
    }
  }, [isOpen, existingTest]);

  // Load existing test data when provided and auto-start recording
  useEffect(() => {
    const resumeRecording = async () => {
      console.log('[LiveRecorder resume useEffect] Checking resume conditions:', {
        hasExistingTest: !!existingTest,
        isOpen,
        hasSessionId: !!sessionId,
        currentStep: step
      });
      
      if (existingTest && isOpen && !sessionId && step === 'loading') {
        console.log('[LiveRecorder resume useEffect] All conditions met, starting resume process');
        setIsResuming(true);
        setResumingTestId(existingTest.id);
        setTestName(existingTest.name || '');
        setTestDescription(existingTest.description || '');
        
        // Get starting URL from test steps - USE THE LAST URL the user was on
        let url = '';
        
        // Strategy 1: Look for the LAST navigation step (where user ended up)
        const navSteps = existingTest.steps?.filter(s => 
          s.action === 'navigate' || 
          s.action === 'goto' || 
          s.type === 'navigation'
        ) || [];
        
        if (navSteps.length > 0) {
          // Use the LAST navigation step's URL
          const lastNavStep = navSteps[navSteps.length - 1];
          url = lastNavStep.value || lastNavStep.url || '';
          console.log(`[LiveRecorder resume] Found ${navSteps.length} navigation steps, using LAST: ${url}`);
        }
        
        // Strategy 2: Check if any step has a URL field (use the last one)
        if (!url && existingTest.steps && existingTest.steps.length > 0) {
          const stepsWithUrl = existingTest.steps.filter(s => s.url);
          if (stepsWithUrl.length > 0) {
            url = stepsWithUrl[stepsWithUrl.length - 1].url;
            console.log(`[LiveRecorder resume] Using last step URL: ${url}`);
          }
        }
        
        // Strategy 3: Fallback to first navigation step if no last URL found
        if (!url && navSteps.length > 0) {
          url = navSteps[0].value || navSteps[0].url || '';
          console.log(`[LiveRecorder resume] Falling back to first nav URL: ${url}`);
        }
        
        // Strategy 4: Use a default URL if absolutely no URL found
        if (!url) {
          console.warn('LiveRecorder: No URL found in test steps, using default');
          url = 'https://example.com';
          showInfo('No starting URL found in test. Using default URL. You can navigate to any page after recording starts.');
        }
        
        console.log(`LiveRecorder: Resuming test "${existingTest.name}" from LAST URL: ${url}`);
        
        setStartingUrl(url);
        setLoadingMessage('Resuming recording session...');
        showInfo(`Resuming test: ${existingTest.name}`);
        
        try {
          // Initialize recording session with existing test data
          const payload = {
            starting_url: url,
            test_id: existingTest.id,
            existing_steps: existingTest.steps || []
          };
          
          console.log('[LiveRecorder resume] Sending resume request with:', {
            test_id: payload.test_id,
            test_name: existingTest.name,
            steps_count: payload.existing_steps.length,
            first_step: payload.existing_steps[0]
          });
          
          const response = await axios.post(
            `${BACKEND_URL}/api/live-recording/start`,
            payload,
            { headers: getAuthHeaders() }
          );

          if (response.data.success) {
            const newSessionId = response.data.session_id;
            setSessionId(newSessionId);
            setCurrentUrl(url);
            setStep('recording');
            
            // Use the same WebSocket initialization as new recordings
            initializeWebSocket(newSessionId);
            showSuccess('Resumed recording session');
          }
        } catch (error) {
          console.error('Failed to resume recording:', error);
          showError(error.response?.data?.detail || 'Failed to resume recording session');
          setLoadingMessage('');
          setStep('setup'); // Fall back to setup on error
        }
      }
    };
    
    resumeRecording();
  }, [existingTest, isOpen, step, sessionId]);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  // Initialize WebSocket connection
  const initializeWebSocket = (sessionId) => {
    // Convert HTTP/HTTPS to WS/WSS
    const wsUrl = BACKEND_URL.replace('http://', 'ws://').replace('https://', 'wss://');
    const socketUrl = `${wsUrl}/api/live-recording/ws/${sessionId}`;
    
    console.log('Connecting to WebSocket:', socketUrl);
    
    const newSocket = new WebSocket(socketUrl);

    newSocket.onopen = () => {
      console.log('WebSocket connected');
      setIsConnected(true);
      showSuccess('Connected to recording session');
    };

    newSocket.onclose = () => {
      console.log('WebSocket disconnected');
      setIsConnected(false);
    };

    newSocket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        handleWebSocketMessage(message);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    newSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      showError('Connection error. Please try again.');
      setIsRecording(false);
    };

    setSocket(newSocket);
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (message) => {
    try {
      const data = typeof message === 'string' ? JSON.parse(message) : message;
      
      switch (data.type) {
        case 'initializing':
          setLoadingMessage(data.message);
          showInfo(data.message);
          break;
          
        case 'session_started':
          showSuccess('Recording session started!');
          setIsRecording(true);
          setIsBrowserReady(true); // Browser proxy is now ready
          setLoadingMessage('');
          setCurrentUrl(data.url);
          setIframeKey(prev => prev + 1); // Force iframe reload
          break;
          
        case 'url_changed':
          console.log('URL changed:', data.url);
          setCurrentUrl(data.url);
          setIframeKey(prev => prev + 1); // Force iframe reload with new URL
          break;
        
        case 'content_changed':
          // Page content changed but URL stayed the same (e.g., form validation errors)
          console.log('Content changed (same URL):', data.url);
          setIframeKey(prev => prev + 1); // Force iframe reload to show updated content
          break;
        
        case 'recording_paused':
          console.log('Recording paused at:', data.current_url);
          setCurrentUrl(data.current_url);
          break;
        
        case 'recording_resumed':
          console.log('Recording resumed from:', data.current_url);
          setCurrentUrl(data.current_url);
          setIsRecording(true);
          // Don't reload iframe - we want to stay on current URL
          break;
          
        case 'existing_steps_loaded':
          console.log('[LiveRecorder WebSocket] Received existing steps:', data.steps.length, 'steps');
          console.log('[LiveRecorder WebSocket] First step:', data.steps[0]);
          setSteps(data.steps.map((step, index) => ({ 
            ...step, 
            step_number: index + 1,
            _key: step.id || `existing-${index}-${Date.now()}`
          })));
          showInfo(`Loaded ${data.count} existing steps`);
          break;
          
        case 'step_recorded':
          setSteps(prev => {
            const newSteps = [...prev, { 
              ...data.step, 
              step_number: data.step_number || prev.length + 1,
              _key: data.step.id || `recorded-${Date.now()}-${Math.random()}`
            }];
            
            // Track the index for type steps so we can update them later
            if (data.step.type === 'fill' || data.step.type === 'type') {
              const selector = data.step.selector;
              if (selector) {
                lastInputStepIndex.current[selector] = newSteps.length - 1;
              }
            }
            
            return newSteps;
          });
          break;
          
        case 'step_updated':
          setSteps(prev => {
            const newSteps = [...prev];
            newSteps[data.step_index] = { 
              ...data.step, 
              step_number: data.step_index + 1,
              _key: prev[data.step_index]._key || data.step.id || `updated-${data.step_index}`
            };
            return newSteps;
          });
          // Don't show notification for automatic updates (consolidation)
          // Only clear editingStepIndex if it was a manual edit
          if (editingStepIndex === data.step_index) {
            showSuccess('Step updated successfully');
            setEditingStepIndex(null);
          }
          break;
          
        case 'step_deleted':
          setSteps(prev => prev.filter((_, index) => index !== data.step_index).map((step, idx) => ({
            ...step,
            step_number: idx + 1
          })));
          showSuccess('Step deleted');
          break;
          
        case 'steps_reordered':
          setSteps(data.steps.map((step, index) => ({ 
            ...step, 
            step_number: index + 1,
            _key: step._key || step.id || `reordered-${index}-${Date.now()}`
          })));
          showSuccess('Steps reordered');
          break;
          
        case 'screenshot':
          console.log('Screenshot received, size:', data.data?.length || 0, 'bytes');
          setCurrentScreenshot(data.data);
          setCurrentUrl(data.url);
          // Draw to canvas for smoother rendering
          drawToCanvas(data.data);
          break;
          
        case 'session_stopped':
          showInfo(`Recording stopped. Total steps: ${data.total_steps}`);
          setIsRecording(false);
          setStep('saving');
          break;
          
        case 'error':
          showError(data.message);
          setIsRecording(false);
          setLoadingMessage('');
          break;
          
        default:
          console.log('Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
    }
  };

  // Start recording
  const startRecording = async () => {
    try {
      if (!startingUrl) {
        showWarning('Please enter a starting URL');
        return;
      }

      // Validate URL
      try {
        new URL(startingUrl);
      } catch {
        showError('Please enter a valid URL (including http:// or https://)');
        return;
      }

      showInfo('Initializing recording session...');

      // Initialize recording session
      const payload = {
        starting_url: startingUrl,
        environment_id: selectedEnvironment?.id
      };
      
      // Include existing test data if resuming
      if (isResuming && resumingTestId && existingTest) {
        payload.test_id = resumingTestId;
        payload.existing_steps = existingTest.steps || [];
        showInfo(`Resuming test with ${payload.existing_steps.length} existing steps`);
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/live-recording/start`,
        payload,
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        const sessionId = response.data.session_id;
        setSessionId(sessionId);
        setStep('recording');
        
        // Initialize WebSocket connection
        initializeWebSocket(sessionId);
      }
    } catch (error) {
      console.error('Failed to start recording:', error);
      showError(error.response?.data?.detail || 'Failed to start recording session');
    }
  };

  // Stop recording
  const stopRecording = () => {
    // Don't close the WebSocket - just pause recording
    // The socket will be closed when user clicks "Save & Finish" or exits
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ type: 'stop_recording' }));
    }
    setIsRecording(false);
    setStep('saving');
  };

  // Step editing functions
  const startEditingStep = (index) => {
    const step = steps[index];
    setEditingStepIndex(index);
    setEditFormData({
      description: step.description || '',
      selector: step.selector || '',
      selectors: step.selectors || [],
      value: step.value || '',
      action: step.action || step.type || '',
      type: step.type || '',
      // Include assertion-specific fields
      expected_text: step.expected_text || '',
      expected_value: step.expected_value || '',
      captured_value: step.captured_value || '',
      assertion_type: step.assertion_type || ''
    });
  };

  const cancelEditingStep = () => {
    setEditingStepIndex(null);
    setEditFormData({});
  };

  const saveStepEdit = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'update_step',
        step_index: editingStepIndex,
        updates: editFormData
      }));
    }
  };

  const deleteStep = (index) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: 'delete_step',
        step_index: index
      }));
    }
  };

  const addCustomStep = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      const customStep = {
        type: 'custom',
        description: 'Custom action',
        action: 'custom'
      };
      socket.send(JSON.stringify({
        type: 'add_custom_step',
        step_data: customStep
      }));
    }
  };

  // Listen for messages from iframe (clicks, form submissions, etc.)
  useEffect(() => {
    const handleIframeMessage = (event) => {
      if (!socket || socket.readyState !== WebSocket.OPEN) return;
      
      const { type, x, y, target, href, sessionId: msgSessionId, subtype, selector, value, text, buttonText } = event.data;
      
      if (msgSessionId !== sessionId) return; // Only handle our session
      
      if (type === 'click') {
        console.log(`Click on ${target} at (${x}, ${y}), text: ${buttonText || text}`);
        
        // Send click to backend
        socket.send(JSON.stringify({
          type: 'click',
          x: x,
          y: y,
          target: target,
          selector: selector || target,
          href: href,
          buttonText: buttonText || text
        }));
      } else if (type === 'input') {
        console.log(`Input on ${target} (${selector}): ${value}`);
        
        // Debounce input events to group keystrokes into a single "type" action
        // Clear existing timer for this selector
        if (inputDebounceTimers.current[selector]) {
          clearTimeout(inputDebounceTimers.current[selector]);
        }
        
        // Set new timer - wait 500ms after user stops typing
        inputDebounceTimers.current[selector] = setTimeout(() => {
          // Check if we already have a type step for this selector
          const existingStepIndex = lastInputStepIndex.current[selector];
          
          if (existingStepIndex !== undefined && steps[existingStepIndex]) {
            // Update the existing step instead of creating a new one
            console.log(`Updating existing type step for ${selector} at index ${existingStepIndex}`);
            socket.send(JSON.stringify({
              type: 'update_step',
              step_index: existingStepIndex,
              updates: {
                value: value,
                description: `Type "${value}" into ${selector}`
              }
            }));
          } else {
            // Create new step and remember its index
            console.log(`Creating new type step for ${selector}`);
            const newStepIndex = steps.length;
            lastInputStepIndex.current[selector] = newStepIndex;
            
            socket.send(JSON.stringify({
              type: 'type',
              x: x,
              y: y,
              selector: selector,
              value: value,
              target: target
            }));
          }
          
          // Clean up timer reference
          delete inputDebounceTimers.current[selector];
        }, 500);
      } else if (type === 'change' && subtype === 'select') {
        console.log(`Select changed: ${text} (value: ${value}) at (${x}, ${y})`);
        
        // Send select change to backend
        socket.send(JSON.stringify({
          type: 'select',
          x: x,
          y: y,
          selector: selector,
          value: value,
          text: text,
          target: target
        }));
      } else if (type === 'submit') {
        // Handle form submission from iframe
        const { buttonText, formAction, formMethod } = event.data;
        console.log(`Form submit: ${buttonText} (action: ${formAction})`);
        
        // Send form submit to backend
        socket.send(JSON.stringify({
          type: 'submit',
          selector: selector,
          buttonText: buttonText,
          formAction: formAction,
          formMethod: formMethod
        }));
      } else if (type === 'loaded') {
        console.log('Page loaded in iframe:', event.data.url);
      } else if (type === 'validation') {
        // Handle validation/assertion messages from iframe context menu
        const { action, selector: validationSelector, text: validationText, value: validationValue, tagName, elementData } = event.data;
        console.log(`Validation action: ${action} on ${tagName} (${validationSelector})`, elementData);
        
        // Map action to assertion type
        const assertionTypeMap = {
          'validate-text': 'text',
          'validate-value': 'value',
          'validate-visible': 'visible',
          'validate-exists': 'exists',
          'validate-attribute': 'attribute'
        };
        
        const assertionType = assertionTypeMap[action] || 'exists';
        
        // Get the text/value with fallbacks from elementData
        const finalText = validationText || elementData?.innerText || '';
        const finalValue = validationValue || elementData?.value || '';
        
        console.log(`Assertion text: "${finalText}", value: "${finalValue}"`);
        
        // Send assertion to backend (check socket is open first)
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify({
            type: 'assertion',
            assertion_type: assertionType,
            selector: validationSelector,
            expected_text: finalText,
            expected_value: finalValue,
            tag_name: tagName,
            element_data: elementData || {}  // Include full element data for potential reuse
          }));
          
          // Show local notification
          showSuccess(`Added ${assertionType} validation for ${tagName}`);
        } else {
          console.error('WebSocket not connected, cannot send assertion');
          showError('Connection lost. Please restart recording.');
        }
      }
    };
    
    window.addEventListener('message', handleIframeMessage);
    return () => window.removeEventListener('message', handleIframeMessage);
  }, [socket, sessionId]);

  // Save test
  const saveTest = async (continueRecording = false) => {
    try {
      if (!testName.trim()) {
        showWarning('Please enter a test name');
        return;
      }

      setIsSaving(true);

      // Set status based on button clicked
      // continueRecording = true (Save & Continue) → draft
      // continueRecording = false (Save & Finish) → active
      const status = continueRecording ? 'draft' : 'active';

      const payload = {
        session_id: sessionId,
        test_name: testName.trim(),
        test_description: testDescription.trim(),
        status: status
      };
      
      // Include test_id if updating existing test
      if (resumingTestId) {
        payload.test_id = resumingTestId;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/live-recording/save`,
        payload,
        { headers: getAuthHeaders() }
      );

      if (response.data.success) {
        const message = response.data.is_update ? 
          'Test updated successfully!' : 
          'Test saved successfully!';
        showSuccess(message);
        
        // Set the test_id for future saves (convert to update mode)
        if (response.data.test_id && !resumingTestId) {
          setResumingTestId(response.data.test_id);
          setIsResuming(true);
        }
        
        if (continueRecording) {
          // Stay in recording mode, switch back to recording view
          setStep('recording');
          setIsRecording(true); // Resume recording state
          
          // Tell backend to resume recording
          if (socket && socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ type: 'resume_recording' }));
          }
          
          showInfo('You can continue recording more steps');
        } else {
          // Close and exit
          handleClose();
        }
      }
    } catch (error) {
      console.error('Failed to save test:', error);
      showError(error.response?.data?.detail || 'Failed to save test');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle close
  const handleClose = () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      // Tell backend to end the session before closing
      socket.send(JSON.stringify({ type: 'end_session' }));
      socket.close();
      setSocket(null);
    }
    
    // Reset state
    setStep('setup');
    setSessionId(null);
    setIsConnected(false);
    setIsRecording(false);
    setIsBrowserReady(false);
    setSteps([]);
    setCurrentUrl('');
    setStartingUrl('');
    setSelectedEnvironment(null);
    setTestName('');
    setTestDescription('');
    setLoadingMessage('');
    setIframeKey(0);
    setIsResuming(false);
    setResumingTestId(null);
    setEditingStepIndex(null);
    setEditFormData({});
    
    onClose();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (socket && socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
    };
  }, [socket]);

  // Get step icon
  const getStepIcon = (stepType) => {
    switch (stepType) {
      case 'click':
        return <MousePointer className="h-4 w-4 text-blue-600" />;
      case 'fill':
      case 'type':
        return <Type className="h-4 w-4 text-green-600" />;
      case 'navigation':
        return <Globe className="h-4 w-4 text-purple-600" />;
      case 'assert':
        return <CheckCircle className="h-4 w-4 text-orange-600" />;
      default:
        return <ChevronRight className="h-4 w-4 text-gray-600" />;
    }
  };

  // Get step color
  const getStepColor = (stepType) => {
    switch (stepType) {
      case 'click':
        return 'bg-blue-50 border-blue-200';
      case 'fill':
      case 'type':
        return 'bg-green-50 border-green-200';
      case 'assert':
        return 'bg-orange-50 border-orange-200';
      case 'navigation':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  // Render full page view when recording (not in modal)
  // Use React Portal to render at document.body level for true full-screen overlay
  if (isOpen && step === 'recording') {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-white" style={{ zIndex: 9999 }}>
        {/* Recording Step - Full Page */}
        <div className="flex flex-col h-screen w-screen">
          <div className="px-6 py-4 border-b flex items-center justify-between bg-white shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse" />
              <h2 className="text-lg font-semibold">Recording in Progress</h2>
              <Badge variant="outline" className="ml-2">
                {steps.length} steps
              </Badge>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (!testName.trim()) {
                    showWarning('Please enter a test name first');
                    setStep('saving');
                    return;
                  }
                  saveTest(true);
                }}
                variant="outline"
                size="sm"
                disabled={!isConnected || isSaving}
                className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
              >
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Progress'}
              </Button>
              <Button
                onClick={stopRecording}
                variant="outline"
                size="sm"
                disabled={!isConnected}
                className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
              >
                <Square className="h-4 w-4 mr-2" />
                Stop Recording
              </Button>
              <Button
                onClick={() => {
                  if (window.confirm('Are you sure you want to exit? Unsaved changes will be lost.')) {
                    handleClose();
                  }
                }}
                variant="ghost"
                size="sm"
                className="text-gray-600 hover:text-gray-900"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex-1 flex overflow-hidden">
            {/* Left Panel - Real Browser (iframe) */}
            <div className="flex-1 bg-gray-900 flex items-center justify-center p-4">
              {currentUrl && sessionId && isBrowserReady ? (
                <div className="w-full h-full flex flex-col">
                  <div className="bg-gray-800 px-4 py-2 rounded-t-lg text-white text-sm flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Globe className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{currentUrl}</span>
                    </div>
                    <span className="text-xs text-green-400 whitespace-nowrap flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      Live Browser
                    </span>
                  </div>
                  <div className="flex-1 bg-white overflow-hidden">
                    <iframe
                      key={iframeKey}
                      ref={iframeRef}
                      src={`${BACKEND_URL}/api/browser-proxy/view/${sessionId}`}
                      className="w-full h-full border-0"
                      title="Live Browser"
                      sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                      style={{ 
                        border: 'none',
                        width: '100%',
                        height: '100%'
                      }}
                    />
                  </div>
                  <div className="bg-gray-800 px-4 py-2 rounded-b-lg text-xs text-gray-400 text-center flex items-center justify-center gap-4">
                    <span className="text-green-400">✓ Real Browser</span>
                    <span>•</span>
                    <span>Click, type, navigate naturally</span>
                    <span>•</span>
                    <span>All actions recorded</span>
                  </div>
                </div>
              ) : (
                <div className="text-center">
                  <Loader2 className="h-12 w-12 text-white animate-spin mx-auto mb-4" />
                  <p className="text-white text-lg font-medium mb-2">
                    {loadingMessage || 'Loading browser...'}
                  </p>
                  <p className="text-gray-400 text-sm">This may take a few moments...</p>
                </div>
              )}
            </div>

            {/* Right Panel - Steps List */}
            <div className="w-96 bg-gray-50 border-l flex flex-col h-full">
              <div className="px-4 py-3 bg-white border-b flex-shrink-0">
                <h3 className="font-semibold text-gray-900">Recorded Steps</h3>
                <p className="text-sm text-gray-600">{steps.length} steps captured</p>
              </div>

              <div className="flex-1 overflow-y-scroll p-4 space-y-2 min-h-0">
                {steps.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p className="text-sm">No steps recorded yet</p>
                    <p className="text-xs mt-1">Start interacting with the browser</p>
                  </div>
                ) : (
                  steps.map((step, index) => (
                    <Card 
                      key={step._key || step.id || `step-${index}`}
                      className={`p-3 ${getStepColor(step.type)}`}
                    >
                      {editingStepIndex === index ? (
                        /* Edit Mode */
                        <div className="space-y-3">
                          <div className="flex items-center justify-between mb-2">
                            <Badge variant="outline" className="text-xs">
                              Step {step.step_number}
                            </Badge>
                            <span className="text-xs font-medium uppercase text-gray-700">
                              EDITING
                            </span>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium mb-1">Description</label>
                            <Input
                              value={editFormData.description || ''}
                              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                              placeholder="Step description"
                              className="text-xs"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium mb-1">Selector</label>
                            {editFormData.selectors && editFormData.selectors.length > 0 ? (
                              <select
                                value={editFormData.selector || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, selector: e.target.value })}
                                className="w-full text-xs border rounded px-2 py-1.5 font-mono"
                              >
                                {editFormData.selectors.map((sel, i) => (
                                  <option key={i} value={sel.value}>
                                    {sel.value} ({sel.method}) - {(sel.confidence * 100).toFixed(0)}%
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <Input
                                value={editFormData.selector || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, selector: e.target.value })}
                                placeholder="Element selector"
                                className="text-xs font-mono"
                              />
                            )}
                          </div>
                          
                          {(step.type === 'fill' || step.type === 'select') && (
                            <div>
                              <label className="block text-xs font-medium mb-1">Value</label>
                              <Input
                                value={editFormData.value || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, value: e.target.value })}
                                placeholder="Input value"
                                className="text-xs"
                              />
                            </div>
                          )}
                          
                          {/* Expected text field for assertion steps */}
                          {step.type === 'assertion' && (
                            <div>
                              <label className="block text-xs font-medium mb-1 text-green-700">Expected Text</label>
                              <Input
                                value={editFormData.expected_text || ''}
                                onChange={(e) => setEditFormData({ ...editFormData, expected_text: e.target.value })}
                                placeholder="Text to validate"
                                className="text-xs border-green-300 focus:border-green-500"
                              />
                              {editFormData.captured_value && editFormData.captured_value !== editFormData.expected_text && (
                                <p className="text-xs text-blue-600 mt-1">
                                  Captured: "{editFormData.captured_value}"
                                </p>
                              )}
                            </div>
                          )}
                          
                          <div className="flex gap-2 pt-2">
                            <Button
                              size="sm"
                              onClick={saveStepEdit}
                              className="flex-1 text-xs"
                            >
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditingStep}
                              className="flex-1 text-xs"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <div className="flex items-start gap-2">
                          <div className="flex-shrink-0 mt-0.5">
                            {getStepIcon(step.type)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {step.step_number}
                              </Badge>
                              <span className="text-xs font-medium uppercase text-gray-700">
                                {step.type}
                              </span>
                            </div>
                            {step.description && (
                              <p className="text-sm text-gray-800 font-medium mb-1">
                                {step.description}
                              </p>
                            )}
                            {step.selector && (
                              <p className="text-xs text-gray-600 font-mono truncate">
                                {getSelectorValue(step.selector)}
                              </p>
                            )}
                            {step.value && (
                              <p className="text-xs text-gray-600 mt-1">
                                Value: <span className="font-medium">{step.value}</span>
                              </p>
                            )}
                            {/* Show expected text for assertion steps */}
                            {step.type === 'assertion' && step.expected_text && (
                              <p className="text-xs text-green-700 mt-1 bg-green-50 px-2 py-1 rounded">
                                Expected: <span className="font-medium">"{step.expected_text}"</span>
                              </p>
                            )}
                            {/* Show captured value for assertion steps if different from expected */}
                            {step.type === 'assertion' && step.captured_value && step.captured_value !== step.expected_text && (
                              <p className="text-xs text-blue-700 mt-1 bg-blue-50 px-2 py-1 rounded">
                                Captured: <span className="font-medium">"{step.captured_value}"</span>
                              </p>
                            )}
                            {step.selectors && step.selectors.length > 1 && (
                              <p className="text-xs text-blue-600 mt-1">
                                +{step.selectors.length - 1} fallback{step.selectors.length > 2 ? 's' : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEditingStep(index)}
                              className="h-7 w-7 p-0"
                              title="Edit step"
                            >
                              ✏️
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteStep(index)}
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete step"
                            >
                              🗑️
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card>
                  ))
                )}
                
                {/* Add Custom Step Button */}
                {isRecording && (
                  <div className="px-4 pb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addCustomStep}
                      className="w-full text-xs"
                    >
                      + Add Custom Step
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Render modal for setup, loading, and saving steps
  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] h-[95vh] p-0">
        {/* Loading Step - Show when resuming */}
        {step === 'loading' && (
          <div className="flex flex-col h-full">
            <DialogHeader className="px-6 py-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-purple-600" />
                Resuming Recording Session
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 flex flex-col items-center justify-center p-6">
              <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
              <p className="text-gray-600 text-center max-w-md">
                {loadingMessage || `Loading "${testName}" with existing steps`}
              </p>
            </div>
          </div>
        )}
        
        {/* Setup Step */}
        {step === 'setup' && (
          <div className="flex flex-col h-full">
            <DialogHeader className="px-6 py-4 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Play className="h-5 w-5 text-red-600" />
                Start Live Test Recording
              </DialogTitle>
            </DialogHeader>

            <div className="flex-1 overflow-auto p-6">
              <div className="max-w-2xl mx-auto space-y-6">
                {/* URL Selection */}
                <Card className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Choose Starting Point</h3>
                  
                  <div className="space-y-4">
                    {/* Custom URL Option */}
                    <div>
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={useCustomUrl}
                          onChange={() => {
                            setUseCustomUrl(true);
                            setSelectedEnvironment(null);
                          }}
                          className="w-4 h-4"
                        />
                        <span className="font-medium">Custom URL</span>
                      </label>
                      
                      {useCustomUrl && (
                        <div className="mt-3">
                          <Input
                            type="url"
                            placeholder="https://example.com"
                            value={startingUrl}
                            onChange={(e) => setStartingUrl(e.target.value)}
                            className="w-full"
                          />
                          <p className="text-sm text-gray-500 mt-1">
                            Enter the URL where you want to start recording
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Environment Option */}
                    {environments.length > 0 && (
                      <div>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            checked={!useCustomUrl}
                            onChange={() => setUseCustomUrl(false)}
                            className="w-4 h-4"
                          />
                          <span className="font-medium">Use Existing Environment</span>
                        </label>
                        
                        {!useCustomUrl && (
                          <div className="mt-3 space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2">
                            {environments.map((env) => (
                              <div
                                key={env.id}
                                onClick={() => {
                                  setSelectedEnvironment(env);
                                  setStartingUrl(env.base_url);
                                }}
                                className={`p-3 border rounded-lg cursor-pointer transition ${
                                  selectedEnvironment?.id === env.id
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <div className="font-medium">{env.name}</div>
                                <div className="text-sm text-gray-600">{env.base_url}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Card>

                {/* Info Card */}
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <h4 className="font-medium text-blue-900 mb-2">How it works:</h4>
                  <ul className="text-sm text-blue-800 space-y-1">
                    <li>• A live browser will open where you can interact</li>
                    <li>• Every click, input, and navigation will be recorded</li>
                    <li>• Steps will appear in real-time on the right panel</li>
                    <li>• Click "Stop Recording" when done, then save your test</li>
                  </ul>
                </Card>
              </div>
            </div>

            <div className="px-6 py-4 border-t flex justify-between">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                onClick={startRecording}
                className="bg-red-600 hover:bg-red-700"
                disabled={!startingUrl}
              >
                <Play className="h-4 w-4 mr-2" />
                Start Recording
              </Button>
            </div>
          </div>
        )}

        {/* Recording Step - Now handled as full page view above, not in modal */}

        {/* Saving Step - Redesigned with Fixed Header/Footer */}
        {step === 'saving' && (
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Fixed Header */}
            <div className="flex-shrink-0 px-6 py-4 border-b bg-gradient-to-r from-green-50 to-blue-50">
              <DialogTitle className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Save className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Save Recorded Test</h2>
                  <p className="text-sm text-gray-600 mt-0.5">{steps.length} steps captured • Ready to save</p>
                </div>
              </DialogTitle>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto min-h-0">
              <div className="p-6 space-y-6">
                {/* Test Information Section */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">1</div>
                    <span>Test Information</span>
                  </div>
                  
                  <Card className="p-5 border-2 border-purple-100 shadow-sm">
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-900">
                          Test Name <span className="text-red-500">*</span>
                        </label>
                        <Input
                          placeholder="e.g., Login Flow Test"
                          value={testName}
                          onChange={(e) => setTestName(e.target.value)}
                          className="text-base border-gray-300 focus:border-purple-400 focus:ring-purple-400"
                          autoFocus
                        />
                        {!testName.trim() && (
                          <p className="text-xs text-gray-500 mt-1">Required: Give your test a descriptive name</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-900">
                          Description <span className="text-gray-400 font-normal">(Optional)</span>
                        </label>
                        <Textarea
                          placeholder="Describe what this test does, expected behavior, or any notes..."
                          value={testDescription}
                          onChange={(e) => setTestDescription(e.target.value)}
                          rows={3}
                          className="text-sm border-gray-300 focus:border-purple-400 focus:ring-purple-400"
                        />
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Recording Summary */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <div className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">2</div>
                    <span>Recording Summary</span>
                  </div>
                  
                  <Card className="p-5 bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 shadow-sm">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 font-medium">Total Steps</p>
                          <p className="text-lg font-bold text-gray-900">{steps.length}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3 sm:col-span-2">
                        <div className="p-2 bg-white rounded-lg shadow-sm">
                          <Globe className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-600 font-medium">Starting URL</p>
                          <p className="text-sm font-semibold text-gray-900 truncate">{startingUrl}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* Steps Preview */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                    <div className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-bold">3</div>
                    <span>Recorded Steps Preview</span>
                  </div>
                  
                  <Card className="border-2 border-blue-100 shadow-sm">
                    <div className="p-4 border-b bg-gray-50">
                      <p className="text-xs text-gray-600">
                        Review your recorded steps below. These will be executed in order when you run this test.
                      </p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {steps.length === 0 ? (
                        <div className="p-8 text-center">
                          <p className="text-gray-500">No steps recorded yet</p>
                        </div>
                      ) : (
                        <div className="divide-y">
                          {steps.map((step, index) => (
                            <div 
                              key={step._key || step.id || `preview-step-${index}`} 
                              className="p-4 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-start gap-3">
                                <Badge 
                                  variant="outline" 
                                  className="text-xs font-bold px-2 py-1 bg-blue-50 text-blue-700 border-blue-300"
                                >
                                  {index + 1}
                                </Badge>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 uppercase">
                                      {step.type}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-700 leading-relaxed">
                                    {step.type === 'type' && step.value ? (
                                      <>
                                        Type <span className="font-semibold text-gray-900">"{step.value}"</span> into{' '}
                                        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{getSelectorValue(step.selector)}</code>
                                      </>
                                    ) : step.type === 'select' ? (
                                      <>
                                        Select <span className="font-semibold text-gray-900">"{step.text || step.value}"</span> from{' '}
                                        <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{getSelectorValue(step.selector)}</code>
                                      </>
                                    ) : step.type === 'click' ? (
                                      <>
                                        Click on <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{getSelectorValue(step.selector)}</code>
                                      </>
                                    ) : step.type === 'navigate' ? (
                                      <>
                                        Navigate to <span className="font-semibold text-gray-900">{step.url}</span>
                                      </>
                                    ) : (
                                      <>
                                        {step.type}: {getSelectorValue(step.selector) || step.url}
                                      </>
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Card>
                </div>
              </div>
            </div>

            {/* Fixed Footer */}
            <div className="flex-shrink-0 px-6 py-4 border-t bg-gray-50 shadow-lg">
              <div className="flex items-center justify-between">
                <Button 
                  variant="outline" 
                  onClick={handleClose}
                  className="border-gray-300 hover:bg-gray-100"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => saveTest(true)}
                    variant="outline"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 hover:border-purple-400"
                    disabled={!testName.trim() || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save & Continue
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => saveTest(false)}
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white shadow-md"
                    disabled={!testName.trim() || isSaving}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Save & Finish
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LiveRecorder;
